import { Injectable } from "@angular/core";

import { Observable, of, throwError } from "rxjs";
import { tap } from "rxjs/internal/operators/tap";
import { catchError, switchMap } from "rxjs/operators";

import { NotificationPaneService } from "../../components/notification-pane/notification-pane.service";
import { ExecutionResultModel } from "../../models/api/execution-result.model";

@Injectable({
    providedIn: "root"
})
export class ApiResponseDispatcherService {
    private readonly DEFAULT_ERROR_TITLE = "";
    private readonly DEFAULT_ERROR_MESSAGE = "An error has occurred. Please try again or contact the system administrator if the problem persists.";

    public constructor(private _notificationService: NotificationPaneService) {
    }

    public unwrap<TResult>(response$: Observable<ExecutionResultModel<TResult>>): Observable<TResult> {
        response$ = this.dispatchNetworkErrorNotification(response$);
        response$ = this.dispatchApiResponseErrorNotification(response$);
        return this.unwrapExecutionResult(response$);
    }

    private dispatchNetworkErrorNotification<TResult>(response$: Observable<TResult>): Observable<TResult> {
        return response$.pipe(
            catchError(error => {
                this._notificationService.showError(this.DEFAULT_ERROR_TITLE, this.DEFAULT_ERROR_MESSAGE);
                return throwError(error);
            })
        );
    }

    private dispatchApiResponseErrorNotification<TResult>(response$: Observable<ExecutionResultModel<TResult>>): Observable<ExecutionResultModel<TResult>> {
        return response$.pipe(
            tap(result => {
                if (!result.success) {
                    const errors = result.errors.map(error => error.message);

                    if (!errors.length) {
                        errors.push(this.DEFAULT_ERROR_MESSAGE);
                    }

                    this._notificationService.showError(this.DEFAULT_ERROR_TITLE, errors.join("<br />"));
                }
            })
        );
    }

    private unwrapExecutionResult<TResult>(response$: Observable<ExecutionResultModel<TResult>>): Observable<TResult> {
        return response$.pipe(
            switchMap(result => {
                return result.success ? of(result.value) : throwError(result.errors);
            })
        );
    }
}
